/* eslint-disable react/require-default-props */

import * as React from 'react';
import {Platform, StyleProp, Text as RNText, TextStyle} from 'react-native';
import {noop, toInteger} from 'lodash';

import {withTheme} from '/provider/ThemeProvider';
import {withHOC} from '/util/hoc.util';
import testProperties from '/util/testing/testProperties';
import {getFontFamily} from '/util/text.util';

import {emphases, styles, variants} from './Text.style';

interface TextProps {
  getTheme: (key: string) => TextStyle | string;
  emphasis: keyof typeof emphases;
  variant: keyof typeof variants;
  useCompetency: boolean;
  truncate: boolean;
  style: StyleProp<TextStyle>;
  children: React.ReactNode;
  truncateValue: boolean;
  testID: string;
  accessible: boolean;
  isBold: boolean;
  accessibilityRole?: string;
  allowFontScaling?: boolean;
  maxFontSizeMultiplier?: number;
}

export const Text: React.FC<TextProps> = ({
  getTheme = noop,
  emphasis = 'high',
  variant = 'body',
  useCompetency = false,
  truncate = false,
  style = {},
  children = '',
  truncateValue = false,
  testID = '',
  accessible = false,
  isBold = false,
  accessibilityRole = undefined,
  allowFontScaling = false,
  maxFontSizeMultiplier = undefined,
  ...props
}) => {
  const textStyle: StyleProp<TextStyle>[] = [
    styles.base,
    emphases[emphasis],
    variants[variant],
  ];

  if (
    ['xLarge', 'large', 'heading', 'subheading', 'button', 'label'].includes(
      variant,
    )
  ) {
    textStyle.push(getFontFamily('bold') as TextStyle);
  } else {
    textStyle.push(getFontFamily('regular') as TextStyle);
  }

  if (isBold) {
    textStyle.push(getFontFamily('bold') as TextStyle);
  }

  if (useCompetency) {
    textStyle.push(getTheme('competency.textColor') as unknown as TextStyle);
  }

  textStyle.push(style);

  // On web, avoid setting accessibilityRole for heading variants to prevent DOM nesting
  const finalAccessibilityRole =
    Platform.OS === 'web' &&
    ['xLarge', 'large', 'heading', 'subheading', 'button', 'label'].includes(
      variant,
    )
      ? undefined
      : accessibilityRole;

  return (
    <RNText
      accessible={accessible}
      accessibilityRole={finalAccessibilityRole as any}
      // importantForAccessibility="yes"
      style={textStyle}
      allowFontScaling={allowFontScaling}
      maxFontSizeMultiplier={maxFontSizeMultiplier}
      numberOfLines={truncate ? 1 : undefined}
      {...testProperties(testID)}
      {...props}>
      {truncateValue ? toInteger(children) : children}
    </RNText>
  );
};

export default withHOC(withTheme)(Text);
